import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { combatApi, mapApi, monsterApi, spellApi } from '../../../api/adminApi';
import { useTranslation } from '../../../i18n/useTranslation';
import type {
  EnemyEntry,
  MapTemplate,
  MonsterTemplate,
  SpellData,
} from '../../../types/adminTypes';

// ── Sub-components ────────────────────────────────────────────────────────────

/** Single map card in the map picker */
function MapCard({
  map,
  selected,
  onClick,
}: {
  map:      Omit<MapTemplate, 'cells'>;
  selected: boolean;
  onClick:  () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        border:       `2px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 8,
        padding:      '12px 14px',
        cursor:       'pointer',
        background:   selected ? 'rgba(68,204,136,0.12)' : 'var(--surface)',
        transition:   'border-color 0.15s, background 0.15s',
        minWidth:     160,
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 13, color: selected ? 'var(--accent)' : 'var(--text)', marginBottom: 4 }}>
        {map.name || '—'}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
        {map.width} × {map.height}
      </div>
      {map.description && (
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4, fontStyle: 'italic' }}>
          {map.description.slice(0, 60)}{map.description.length > 60 ? '…' : ''}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function LaunchCombatPage() {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  // ── Remote data ─────────────────────────────────────────────────────────────
  const [maps,     setMaps]     = useState<Omit<MapTemplate, 'cells'>[]>([]);
  const [monsters, setMonsters] = useState<MonsterTemplate[]>([]);
  const [spells,   setSpells]   = useState<SpellData[]>([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([mapApi.list(), monsterApi.list(), spellApi.list()])
      .then(([m, mo, s]) => { setMaps(m); setMonsters(mo); setSpells(s); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // ── Form state ───────────────────────────────────────────────────────────────
  const [selectedMapId,    setSelectedMapId]    = useState<string | null>(null);
  const [enemies,          setEnemies]          = useState<EnemyEntry[]>([]);
  const [includePlayer,    setIncludePlayer]    = useState(true);
  const [playerName,       setPlayerName]       = useState('Hero');
  const [playerClass,      setPlayerClass]      = useState<string>('warrior');
  const [playerSpellIds,   setPlayerSpellIds]   = useState<string[]>([]);

  // Pre-select first 3 available spells as hero defaults
  useEffect(() => {
    if (spells.length > 0 && playerSpellIds.length === 0) {
      setPlayerSpellIds(spells.slice(0, 3).map((s) => s.id));
    }
  }, [spells, playerSpellIds.length]);

  // ── Launch state ────────────────────────────────────────────────────────────
  const [launching, setLaunching] = useState(false);
  const [error,     setError]     = useState('');

  // ── Enemy helpers ────────────────────────────────────────────────────────────
  const addEnemy = (templateId: string) => {
    setEnemies((prev) => {
      const existing = prev.find((e) => e.template_id === templateId);
      if (existing) {
        return prev.map((e) =>
          e.template_id === templateId ? { ...e, count: e.count + 1 } : e
        );
      }
      return [...prev, { template_id: templateId, count: 1 }];
    });
  };

  const removeEnemy = (templateId: string) =>
    setEnemies((prev) => prev.filter((e) => e.template_id !== templateId));

  const changeCount = (templateId: string, delta: number) => {
    setEnemies((prev) => {
      const entry = prev.find((e) => e.template_id === templateId);
      if (!entry) return prev;
      const next = entry.count + delta;
      if (next <= 0) return prev.filter((e) => e.template_id !== templateId);
      return prev.map((e) =>
        e.template_id === templateId ? { ...e, count: next } : e
      );
    });
  };

  const togglePlayerSpell = (id: string) =>
    setPlayerSpellIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );

  // ── Launch ───────────────────────────────────────────────────────────────────
  const handleLaunch = async () => {
    if (!selectedMapId) { setError(t('launch.error.mapRequired')); return; }
    if (enemies.length === 0 && !includePlayer) {
      setError(t('launch.error.noEnemies'));
      return;
    }

    setLaunching(true);
    setError('');
    try {
      const r = await combatApi.launch({
        map_id:           selectedMapId,
        enemies,
        include_player:   includePlayer,
        player_name:      playerName.trim() || 'Hero',
        player_class:     playerClass,
        player_spell_ids: playerSpellIds,
      });
      // Navigate directly into the combat board — the exit button in App.tsx
      // will bring the admin back to /admin when the match ends.
      navigate(`/play?combat_id=${r.combat_id}&entity_id=${r.player_entity_id}`);
    } catch (e) {
      setError((e as Error).message);
      setLaunching(false);
    }
  };

  // ── Derived helpers ──────────────────────────────────────────────────────────
  const selectedMap   = maps.find((m) => m.id === selectedMapId);
  const totalEnemies  = enemies.reduce((sum, e) => sum + e.count, 0);
  const monsterById   = Object.fromEntries(monsters.map((m) => [m.id!, m]));

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <>
      <div className="adminHeader"><h1>{t('common.loading')}</h1></div>
      <div className="adminContent"><div className="spinner" /></div>
    </>
  );

  // ── Main form ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="adminHeader">
        <h1>{t('launch.title')}</h1>
      </div>

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        {/* ── 1. Select Map ── */}
        <div className="adminCard">
          <div className="adminCardHeader">{t('launch.selectMap')}</div>
          <div style={{ padding: '14px 18px' }}>
            {maps.length === 0 ? (
              <div className="emptyState" style={{ padding: '20px 0' }}>{t('launch.noMaps')}</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {maps.map((m) => (
                  <MapCard
                    key={m.id}
                    map={m}
                    selected={m.id === selectedMapId}
                    onClick={() => setSelectedMapId(m.id ?? null)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── 2. Enemies ── */}
        <div className="adminCard">
          <div className="adminCardHeader">{t('launch.enemies')}</div>
          <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

            {/* Left: available monsters */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)', marginBottom: 8 }}>
                {t('launch.availableMonsters')}
              </div>
              {monsters.length === 0 ? (
                <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>{t('launch.noMonsters')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {monsters.map((m) => (
                    <div key={m.id} style={{
                      display:        'flex',
                      alignItems:     'center',
                      justifyContent: 'space-between',
                      padding:        '7px 10px',
                      background:     'var(--input-bg)',
                      border:         '1px solid var(--border)',
                      borderRadius:   5,
                      fontSize:       12,
                    }}>
                      <div>
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{m.name}</span>
                        <span style={{ color: 'var(--text-dim)', marginLeft: 8 }}>
                          {t('launch.col.level')}{m.level}
                          &nbsp;·&nbsp;
                          {t('launch.col.hp')} {m.hp_max}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btnSecondary"
                        style={{ padding: '3px 10px', fontSize: 13, fontWeight: 700 }}
                        onClick={() => addEnemy(m.id!)}
                      >
                        +
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right: selected enemies */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)', marginBottom: 8 }}>
                {t('launch.selectedEnemies')}
                {totalEnemies > 0 && (
                  <span className="badge" style={{ marginLeft: 8 }}>{totalEnemies}</span>
                )}
              </div>
              {enemies.length === 0 ? (
                <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>{t('launch.noEnemies')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {enemies.map((entry) => {
                    const m = monsterById[entry.template_id];
                    if (!m) return null;
                    return (
                      <div key={entry.template_id} style={{
                        display:      'flex',
                        alignItems:   'center',
                        gap:          8,
                        padding:      '7px 10px',
                        background:   'rgba(68,204,136,0.07)',
                        border:       '1px solid var(--accent)',
                        borderRadius: 5,
                        fontSize:     12,
                      }}>
                        {/* Count controls */}
                        <button
                          type="button"
                          className="btn btnSecondary"
                          style={{ padding: '2px 8px', minWidth: 26, fontSize: 14 }}
                          onClick={() => changeCount(entry.template_id, -1)}
                        >−</button>
                        <span style={{ fontWeight: 700, color: 'var(--accent)', minWidth: 16, textAlign: 'center' }}>
                          {entry.count}
                        </span>
                        <button
                          type="button"
                          className="btn btnSecondary"
                          style={{ padding: '2px 8px', minWidth: 26, fontSize: 14 }}
                          onClick={() => changeCount(entry.template_id, +1)}
                        >+</button>

                        {/* Name */}
                        <span style={{ flex: 1, fontWeight: 600, color: 'var(--text)' }}>{m.name}</span>

                        {/* Remove */}
                        <button
                          type="button"
                          className="btn btnDanger"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          onClick={() => removeEnemy(entry.template_id)}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* ── 3. Player Hero ── */}
        <div className="adminCard">
          <div className="adminCardHeader">{t('launch.playerHero')}</div>
          <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={includePlayer}
                onChange={(e) => setIncludePlayer(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' }}
              />
              <span>{t('launch.includePlayer')}</span>
            </label>

            {includePlayer && (
              <>
                {/* Name + Class */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <div className="formGroup" style={{ flex: '1 1 200px', maxWidth: 300 }}>
                    <label className="formLabel">{t('launch.heroName')}</label>
                    <input
                      type="text"
                      className="formInput"
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      placeholder="Hero"
                    />
                  </div>
                  <div className="formGroup" style={{ flex: '0 0 160px' }}>
                    <label className="formLabel">Classe</label>
                    <select
                      className="formInput"
                      value={playerClass}
                      onChange={(e) => setPlayerClass(e.target.value)}
                    >
                      <option value="warrior">⚔️ Warrior</option>
                      <option value="mage">🔮 Mage</option>
                      <option value="rogue">🗡️ Rogue</option>
                      <option value="archer">🏹 Archer</option>
                      <option value="cleric">✨ Cleric</option>
                      <option value="ferreiro">🔨 Ferreiro</option>
                    </select>
                  </div>
                </div>

                {/* Spells */}
                {spells.length > 0 && (
                  <div>
                    <div className="formLabel" style={{ marginBottom: 8 }}>{t('launch.heroSpells')}</div>
                    <div className="spellChips">
                      {spells.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className={`spellChip${playerSpellIds.includes(s.id) ? ' selected' : ''}`}
                          onClick={() => togglePlayerSpell(s.id)}
                        >
                          <span>{t(`spell.${s.id}.name` as any) || s.name}</span>
                          <span className="spellCost">{s.ap_cost} AP</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── 4. Summary & Launch ── */}
        <div className="adminCard">
          <div className="adminCardHeader">{t('launch.summary')}</div>
          <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>

            {/* Summary rows */}
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', fontSize: 13 }}>
              <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{t('launch.summaryMap')}</span>
              <span style={{ color: selectedMap ? 'var(--text)' : 'var(--danger)', fontWeight: selectedMap ? 400 : 600 }}>
                {selectedMap
                  ? `${selectedMap.name} (${selectedMap.width}×${selectedMap.height})`
                  : t('launch.noneSelected')}
              </span>

              <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{t('launch.summaryEnemies')}</span>
              <span style={{ color: totalEnemies > 0 ? 'var(--text)' : 'var(--text-dim)' }}>
                {totalEnemies > 0
                  ? enemies.map((e) => `${e.count}× ${monsterById[e.template_id]?.name ?? '?'}`).join(', ')
                  : '—'}
              </span>

              <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>{t('launch.summaryPlayer')}</span>
              <span style={{ color: 'var(--text)' }}>
                {includePlayer
                  ? `${playerName || 'Hero'} — ${t('launch.summaryHero')}`
                  : t('launch.summaryNoPlayer')}
              </span>
            </div>

            {/* Launch button */}
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn btnPrimary"
                style={{ fontSize: 14, padding: '9px 24px' }}
                disabled={launching || !selectedMapId || (enemies.length === 0 && !includePlayer)}
                onClick={() => void handleLaunch()}
              >
                {launching ? t('launch.launching') : t('launch.btn')}
              </button>
            </div>

          </div>
        </div>

      </div>
    </>
  );
}
